import React, { useContext } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DiscoveryContext } from "../../luminaire/BrandAudit/discovery-context";
import { getColorForScore } from "../../utils/helpers";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function AverageSentimentScores({
  category,
  market,
  selectedPersona,
  selectedLLM,
  competitors,
}) {
  const discoveryCtx = useContext(DiscoveryContext);
  const entity = discoveryCtx.entity;
  const isPropensity = discoveryCtx?.isPropensityToBuy?.() ?? (discoveryCtx?.brand_scores?.rbiType === "propensityToBuy");
  const mapTitle = (t) => discoveryCtx?.mapMetricTitle?.(t) ?? t;

  // Get competitor names
  let competitorNames = discoveryCtx?.competitors?.[category]?.map((c) => c.name) ?? [];

  // Collect all rows: brand + competitors
  const allRows = [];
  const competitorRows = [];
  const addedRowNames = new Set(); // Track added row names to prevent duplicates

  // Get brand data
  const brandData =
    discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.["0"]?.extras;

  if (brandData?.favorabilities && brandData.favorabilities.length > 0) {
    brandData.favorabilities.forEach((fav) => {
      if (fav?.name && !addedRowNames.has(fav.name)) {
        allRows.push(fav);
        addedRowNames.add(fav.name);
      }
    });
  }

  // Get competitor data
  if (competitorNames && competitorNames.length > 0) {
    competitorNames.forEach((compName, index) => {
      const compData =
        discoveryCtx.brand_scores[selectedLLM]?.[category]?.[market]?.[selectedPersona]?.[
          index + 1
        ]?.extras;

      if (compData?.favorabilities && compData.favorabilities.length > 0) {
        compData.favorabilities.forEach((fav) => {
          if (fav?.name && !addedRowNames.has(fav.name)) {
            allRows.push(fav);
            addedRowNames.add(fav.name);
            competitorRows.push(fav);
          }
        });
      }
    });
  }

  // Calculate Competitor Average row
  if (competitorRows.length > 0) {
    const competitorAverageRow = { name: "Competitor Average" };

    const sentimentKeys = Object.keys(competitorRows[0]).filter((key) =>
      key.startsWith("sentiment")
    );

    // Calculate average for each sentiment column
    sentimentKeys.forEach((key) => {
      const sum = competitorRows.reduce((acc, row) => {
        const value = parseFloat(row[key]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      const avg = competitorRows.length > 0 ? sum / competitorRows.length : 0;
      competitorAverageRow[key] = avg.toFixed(2);
    });

    // Calculate average of the Avg column
    const avgSum = competitorRows.reduce((acc, row) => {
      const value = parseFloat(row.average);
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
    competitorAverageRow.average = (competitorRows.length > 0 ? avgSum / competitorRows.length : 0).toFixed(2);

    // Calculate average of score column if it exists
    if (competitorRows[0].score !== undefined) {
      const scoreSum = competitorRows.reduce((acc, row) => {
        const value = parseFloat(row.score);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
      competitorAverageRow.score = (competitorRows.length > 0 ? scoreSum / competitorRows.length : 0).toFixed(2);
    }

    allRows.push(competitorAverageRow);
  }

  // Get number of sentiment columns
  const sentimentColumnCount =
    brandData?.favorabilities && brandData.favorabilities.length > 0
      ? Object.keys(brandData.favorabilities[0]).filter((key) => key.startsWith("sentiment")).length
      : 0;

  if (!brandData?.favorabilities || brandData.favorabilities.length === 0) {
    return null;
  }

  // Prepare data for color coding
  const isExclude = true; // Set to false to include Competitor Average in color comparison
  
  // Filter rows for color comparison based on isExclude flag
  const rowsForColorComparison = isExclude 
    ? allRows.filter(row => row.name !== "Competitor Average")
    : allRows;
  
  // Get all values for each sentiment column for color comparison
  const sentimentColumnValues = {};
  for (let i = 0; i < sentimentColumnCount; i++) {
    const colKey = `sentiment${i + 1}`;
    sentimentColumnValues[colKey] = rowsForColorComparison
      .map(row => row[colKey])
      .filter(val => val !== undefined && val !== null);
  }

  // Get all average values for color comparison
  const averageValues = rowsForColorComparison
    .map(row => row.average)
    .filter(val => val !== undefined && val !== null);

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 800 }} aria-label="average sentiment scores">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Insitution Name</StyledTableCell>
              {Array.from({ length: sentimentColumnCount }).map((_, i) => (
                <StyledTableCell align="right" key={i}>
                  Question {i + 1}
                </StyledTableCell>
              ))}
              <StyledTableCell align="right">Avg</StyledTableCell>
              {brandData?.favorabilities?.[0]?.score !== undefined && (
                <StyledTableCell align="right">Score</StyledTableCell>
              )}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {allRows?.map((row, idx) => {
              const isCompetitorAverage = row.name === "Competitor Average";
              return (
                <StyledTableRow
                  key={`${row.name}-${idx}`}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: isCompetitorAverage ? "#e3f2fd" : "inherit",
                    fontWeight: isCompetitorAverage ? "bold" : "normal",
                  }}
                >
                  <StyledTableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal" }}
                  >
                    {row.name}
                  </StyledTableCell>
                  {Array.from({ length: sentimentColumnCount }).map((_, i) => {
                    const colKey = `sentiment${i + 1}`;
                    const cellValue = row[colKey];
                    const cellColor = (!isCompetitorAverage || !isExclude) 
                      ? getColorForScore(cellValue, sentimentColumnValues[colKey]) 
                      : 'inherit';
                    return (
                      <StyledTableCell
                        align="right"
                        key={i}
                        sx={{
                          fontWeight: isCompetitorAverage ? "bold" : "normal",
                          color: cellColor
                        }}
                      >
                        {cellValue}
                      </StyledTableCell>
                    );
                  })}
                  <StyledTableCell
                    align="right"
                    sx={{
                      fontWeight: isCompetitorAverage ? "bold" : "normal",
                      color: (!isCompetitorAverage || !isExclude) 
                        ? getColorForScore(row.average, averageValues) 
                        : 'inherit'
                    }}
                  >
                    {row.average}
                  </StyledTableCell>
                  {row.score !== undefined && (
                    <StyledTableCell
                      align="right"
                      sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal" }}
                    >
                      {row?.score}
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
